import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Building, Users, FileText, Send, Plus, Trash2, Edit, Globe, Phone, Mail,
    Target, TrendingUp, CheckCircle, AlertTriangle, Sparkles, ArrowRight
} from 'lucide-react';

const LOAN_TYPES = ['conventional', 'fha', 'va', 'usda'];
const PROPERTY_TYPES = ['single_family', 'condo', 'townhouse', 'multi_family'];

export default function MortgagePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [lenders, setLenders] = useState([]);
    const [matchedData, setMatchedData] = useState(null);
    const [applications, setApplications] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const isAdmin = user?.role === 'admin';

    const loadData = useCallback(async () => {
        try {
            const [lRes, aRes, sRes] = await Promise.all([
                API.get('/mortgage/lenders'),
                API.get('/mortgage/applications'),
                API.get('/mortgage/submissions').catch(() => ({ data: [] })),
            ]);
            setLenders(lRes.data);
            setApplications(aRes.data);
            setSubmissions(sRes.data);
            try {
                const mRes = await API.get('/mortgage/matched-lenders');
                setMatchedData(mRes.data);
            } catch (e) {}
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="mortgage-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']">Mortgage Center</h1>
                        <p className="text-sm text-zinc-500 mt-1">Get mortgage-ready and submit to lenders</p>
                    </div>
                </div>

                {matchedData && (
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5 mb-6" data-testid="matched-lenders-banner">
                        <div className="flex items-center gap-3 mb-2">
                            <Target className="w-5 h-5 text-emerald-700" />
                            <span className="text-sm font-semibold text-zinc-900 font-['Outfit']">
                                Estimated Score: {matchedData.estimated_score} | {matchedData.matched_lenders?.length || 0} Lenders Match
                            </span>
                        </div>
                        <p className="text-xs text-zinc-500">Based on your credit profile, these lenders accept applicants at your score level.</p>
                    </div>
                )}

                <Tabs defaultValue="lenders" className="space-y-6">
                    <TabsList className="bg-white border border-zinc-200 shadow-sm rounded-xl p-1">
                        <TabsTrigger value="lenders" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="mortgage-tab-lenders">
                            <Building className="w-4 h-4 mr-1" /> Lenders ({lenders.length})
                        </TabsTrigger>
                        <TabsTrigger value="application" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="mortgage-tab-application">
                            <FileText className="w-4 h-4 mr-1" /> Application
                        </TabsTrigger>
                        <TabsTrigger value="submissions" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="mortgage-tab-submissions">
                            <Send className="w-4 h-4 mr-1" /> Submissions ({submissions.length})
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="lenders">
                        <LendersTab lenders={lenders} matchedData={matchedData} isAdmin={isAdmin} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="application">
                        <ApplicationTab lenders={lenders} onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="submissions">
                        <SubmissionsTab applications={applications} submissions={submissions} onRefresh={loadData} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function LendersTab({ lenders, matchedData, isAdmin, onRefresh }) {
    const matchedIds = new Set((matchedData?.matched_lenders || []).map(l => l.id));
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ name: '', type: 'conventional', contact_email: '', contact_phone: '', website: '', min_credit_score: 620, max_dti: 43, notes: '' });

    const addLender = async () => {
        try {
            await API.post('/mortgage/lenders', { ...form, accepts_submissions: true });
            setShowAdd(false);
            setForm({ name: '', type: 'conventional', contact_email: '', contact_phone: '', website: '', min_credit_score: 620, max_dti: 43, notes: '' });
            onRefresh();
        } catch (err) { console.error(err); }
    };

    const deleteLender = async (id) => {
        try {
            await API.delete(`/mortgage/lenders/${id}`);
            onRefresh();
        } catch (err) { console.error(err); }
    };

    return (
        <div className="space-y-4" data-testid="lenders-tab">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Lender Directory</h3>
                {isAdmin && (
                    <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="add-lender-btn">
                        <Plus className="w-3 h-3 mr-1" /> Add Lender
                    </Button>
                )}
            </div>
            {showAdd && (
                <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm space-y-3" data-testid="add-lender-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="border-zinc-200 h-8 text-sm" /></div>
                        <div><Label className="text-xs">Type</Label>
                            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white">
                                {LOAN_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                            </select>
                        </div>
                        <div><Label className="text-xs">Email</Label><Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} className="border-zinc-200 h-8 text-sm" /></div>
                        <div><Label className="text-xs">Phone</Label><Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="border-zinc-200 h-8 text-sm" /></div>
                        <div><Label className="text-xs">Min Score</Label><Input type="number" value={form.min_credit_score} onChange={e => setForm(f => ({ ...f, min_credit_score: parseInt(e.target.value) || 580 }))} className="border-zinc-200 h-8 text-sm" /></div>
                        <div><Label className="text-xs">Max DTI%</Label><Input type="number" value={form.max_dti} onChange={e => setForm(f => ({ ...f, max_dti: parseFloat(e.target.value) || 43 }))} className="border-zinc-200 h-8 text-sm" /></div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={addLender} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Add</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAdd(false)} className="text-xs border-zinc-200">Cancel</Button>
                    </div>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lenders.map(l => (
                    <div key={l.id} className={`bg-white rounded-xl border p-5 shadow-sm ${matchedIds.has(l.id) ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-zinc-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-zinc-400" />
                                <span className="text-sm font-semibold text-zinc-900">{l.name}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Badge className={`text-xs ${l.type === 'fha' ? 'bg-blue-100 text-blue-700' : l.type === 'va' ? 'bg-purple-100 text-purple-700' : l.type === 'conventional' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {l.type.toUpperCase()}
                                </Badge>
                                {matchedIds.has(l.id) && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Match</Badge>}
                            </div>
                        </div>
                        <div className="space-y-1 text-xs text-zinc-500 mb-3">
                            <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {l.contact_email}</div>
                            {l.contact_phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {l.contact_phone}</div>}
                            <div>Min Score: <span className="font-medium text-zinc-700">{l.min_credit_score}</span> | Max DTI: <span className="font-medium text-zinc-700">{l.max_dti}%</span></div>
                        </div>
                        {l.notes && <p className="text-xs text-zinc-400 mb-3">{l.notes}</p>}
                        {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => deleteLender(l.id)} className="text-xs text-red-400 h-6 px-2">
                                <Trash2 className="w-3 h-3 mr-1" /> Remove
                            </Button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ApplicationTab({ lenders, onRefresh }) {
    const [form, setForm] = useState({
        full_name: '', phone: '', email: '', current_address: '', years_at_address: 0,
        employer: '', job_title: '', years_employed: 0, monthly_income: 0, other_income: 0,
        property_address: '', property_type: 'single_family', estimated_value: 0,
        loan_amount: 0, down_payment: 0, monthly_debts: 0, assets_total: 0,
        credit_score_range: '', loan_type: 'conventional', notes: '',
    });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const submit = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await API.post('/mortgage/applications', form);
            // Run AI assessment
            const assessRes = await API.post(`/mortgage/applications/${data.id}/assess`);
            setResult({ application: data, assessment: assessRes.data });
            onRefresh();
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const dti = form.monthly_income > 0 ? ((form.monthly_debts / (form.monthly_income + form.other_income)) * 100).toFixed(1) : 0;
    const ltv = form.estimated_value > 0 ? ((form.loan_amount / form.estimated_value) * 100).toFixed(1) : 0;

    return (
        <div className="space-y-6" data-testid="application-tab">
            <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Mortgage Application (1003 URLA)</h3>
            {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>}

            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm space-y-5">
                <h4 className="text-sm font-semibold text-zinc-900 border-b border-zinc-200 pb-2">Borrower Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div><Label className="text-xs text-zinc-600">Full Name</Label><Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} className="border-zinc-200 h-9 text-sm" data-testid="app-name" /></div>
                    <div><Label className="text-xs text-zinc-600">Phone</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div className="col-span-2"><Label className="text-xs text-zinc-600">Current Address</Label><Input value={form.current_address} onChange={e => setForm(f => ({ ...f, current_address: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Years at Address</Label><Input type="number" value={form.years_at_address || ''} onChange={e => setForm(f => ({ ...f, years_at_address: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                </div>

                <h4 className="text-sm font-semibold text-zinc-900 border-b border-zinc-200 pb-2 pt-2">Employment & Income</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div><Label className="text-xs text-zinc-600">Employer</Label><Input value={form.employer} onChange={e => setForm(f => ({ ...f, employer: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Job Title</Label><Input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Years Employed</Label><Input type="number" value={form.years_employed || ''} onChange={e => setForm(f => ({ ...f, years_employed: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Monthly Income ($)</Label><Input type="number" value={form.monthly_income || ''} onChange={e => setForm(f => ({ ...f, monthly_income: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" data-testid="app-income" /></div>
                    <div><Label className="text-xs text-zinc-600">Other Income ($)</Label><Input type="number" value={form.other_income || ''} onChange={e => setForm(f => ({ ...f, other_income: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                </div>

                <h4 className="text-sm font-semibold text-zinc-900 border-b border-zinc-200 pb-2 pt-2">Property & Loan</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="col-span-2"><Label className="text-xs text-zinc-600">Property Address</Label><Input value={form.property_address} onChange={e => setForm(f => ({ ...f, property_address: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Property Type</Label>
                        <select value={form.property_type} onChange={e => setForm(f => ({ ...f, property_type: e.target.value }))} className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                            {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                        </select>
                    </div>
                    <div><Label className="text-xs text-zinc-600">Estimated Value ($)</Label><Input type="number" value={form.estimated_value || ''} onChange={e => setForm(f => ({ ...f, estimated_value: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Loan Amount ($)</Label><Input type="number" value={form.loan_amount || ''} onChange={e => setForm(f => ({ ...f, loan_amount: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" data-testid="app-loan" /></div>
                    <div><Label className="text-xs text-zinc-600">Down Payment ($)</Label><Input type="number" value={form.down_payment || ''} onChange={e => setForm(f => ({ ...f, down_payment: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Loan Type</Label>
                        <select value={form.loan_type} onChange={e => setForm(f => ({ ...f, loan_type: e.target.value }))} className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                            {LOAN_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                        </select>
                    </div>
                </div>

                <h4 className="text-sm font-semibold text-zinc-900 border-b border-zinc-200 pb-2 pt-2">Financial</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div><Label className="text-xs text-zinc-600">Monthly Debts ($)</Label><Input type="number" value={form.monthly_debts || ''} onChange={e => setForm(f => ({ ...f, monthly_debts: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Total Assets ($)</Label><Input type="number" value={form.assets_total || ''} onChange={e => setForm(f => ({ ...f, assets_total: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    <div><Label className="text-xs text-zinc-600">Credit Score Range</Label><Input value={form.credit_score_range} onChange={e => setForm(f => ({ ...f, credit_score_range: e.target.value }))} placeholder="e.g. 620-680" className="border-zinc-200 h-9 text-sm" /></div>
                </div>

                {/* Live DTI/LTV */}
                <div className="flex gap-4 p-3 bg-zinc-50 rounded-lg">
                    <div className="text-xs"><span className="text-zinc-500">DTI Ratio:</span> <span className={`font-semibold ${dti > 43 ? 'text-red-600' : 'text-emerald-700'}`}>{dti}%</span></div>
                    <div className="text-xs"><span className="text-zinc-500">LTV Ratio:</span> <span className={`font-semibold ${ltv > 95 ? 'text-red-600' : 'text-emerald-700'}`}>{ltv}%</span></div>
                </div>

                <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-8" data-testid="submit-application-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> {loading ? 'Submitting & Assessing...' : 'Submit & AI Assess'}
                </Button>
            </div>

            {result && result.assessment && (
                <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm" data-testid="ai-assessment-result">
                    <h4 className="text-sm font-semibold text-zinc-900 font-['Outfit'] mb-4">AI Mortgage Assessment</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                            <div className="text-xs text-emerald-600 mb-1">Readiness</div>
                            <div className="text-2xl font-bold text-emerald-700 font-['Outfit']">{result.assessment.readiness_score || 0}</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-xs text-blue-600 mb-1">Recommendation</div>
                            <div className="text-sm font-bold text-blue-700">{result.assessment.recommendation || 'N/A'}</div>
                        </div>
                        <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="text-xs text-amber-600 mb-1">Est. Rate</div>
                            <div className="text-sm font-bold text-amber-700">{result.assessment.estimated_rate_range || 'N/A'}</div>
                        </div>
                    </div>
                    {result.assessment.summary && <p className="text-sm text-zinc-700 mb-3">{result.assessment.summary}</p>}
                    <div className="grid grid-cols-2 gap-4">
                        {result.assessment.strengths?.length > 0 && (
                            <div>
                                <div className="text-xs font-medium text-emerald-700 mb-1">Strengths</div>
                                {result.assessment.strengths.map((s, i) => (
                                    <div key={i} className="text-xs text-zinc-600 flex gap-1"><CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />{s}</div>
                                ))}
                            </div>
                        )}
                        {result.assessment.concerns?.length > 0 && (
                            <div>
                                <div className="text-xs font-medium text-red-600 mb-1">Concerns</div>
                                {result.assessment.concerns.map((c, i) => (
                                    <div key={i} className="text-xs text-zinc-600 flex gap-1"><AlertTriangle className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />{c}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function SubmissionsTab({ applications, submissions, onRefresh }) {
    return (
        <div className="space-y-4" data-testid="submissions-tab">
            <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Loan Submissions</h3>
            {applications.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <FileText className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                    <p className="text-sm text-zinc-400">No applications yet. Create one in the Application tab.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {applications.map(a => (
                        <ApplicationCard key={a.id} app={a} submissions={submissions.filter(s => s.application_id === a.id)} onRefresh={onRefresh} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ApplicationCard({ app, submissions, onRefresh }) {
    const [submitLender, setSubmitLender] = useState('');
    const [lenders, setLenders] = useState([]);
    const [showSubmit, setShowSubmit] = useState(false);

    useEffect(() => {
        API.get('/mortgage/lenders').then(r => setLenders(r.data)).catch(() => {});
    }, []);

    const submitToLender = async () => {
        if (!submitLender) return;
        try {
            await API.post(`/mortgage/applications/${app.id}/submit`, { lender_id: submitLender });
            setShowSubmit(false);
            onRefresh();
        } catch (err) { console.error(err); }
    };

    return (
        <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <span className="text-sm font-medium text-zinc-900">{app.full_name}</span>
                    <span className="text-xs text-zinc-500 ml-2">{app.loan_type?.toUpperCase()} | ${(app.loan_amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className={app.status === 'submitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}>{app.status}</Badge>
                    {app.ai_assessment && <Badge className="bg-blue-100 text-blue-700">Score: {app.ai_assessment.readiness_score}</Badge>}
                </div>
            </div>
            <div className="grid grid-cols-4 gap-3 text-xs text-center mb-3">
                <div><span className="text-zinc-400">DTI</span><div className="font-semibold text-zinc-900">{app.dti_ratio}%</div></div>
                <div><span className="text-zinc-400">LTV</span><div className="font-semibold text-zinc-900">{app.ltv_ratio}%</div></div>
                <div><span className="text-zinc-400">Income</span><div className="font-semibold text-zinc-900">${(app.monthly_income || 0).toLocaleString()}/mo</div></div>
                <div><span className="text-zinc-400">Property</span><div className="font-semibold text-zinc-900">${(app.estimated_value || 0).toLocaleString()}</div></div>
            </div>
            {submissions.length > 0 && (
                <div className="mb-3 space-y-1">
                    {submissions.map(s => (
                        <div key={s.id} className="text-xs flex items-center gap-2 p-2 bg-emerald-50 rounded">
                            <Send className="w-3 h-3 text-emerald-600" />
                            <span className="text-zinc-700">Submitted to <strong>{s.lender_name}</strong></span>
                            <span className="text-zinc-400">{new Date(s.submitted_at).toLocaleDateString()}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowSubmit(!showSubmit)} className="text-xs border-emerald-200 text-emerald-700" data-testid={`submit-to-lender-${app.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Submit to Lender
                </Button>
            </div>
            {showSubmit && (
                <div className="mt-3 flex gap-2 items-center">
                    <select value={submitLender} onChange={e => setSubmitLender(e.target.value)} className="border border-zinc-200 rounded-lg px-3 py-1.5 text-sm bg-white flex-1">
                        <option value="">Select lender...</option>
                        {lenders.map(l => <option key={l.id} value={l.id}>{l.name} ({l.type.toUpperCase()})</option>)}
                    </select>
                    <Button size="sm" onClick={submitToLender} disabled={!submitLender} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Submit</Button>
                </div>
            )}
        </div>
    );
}
